
import { Query } from '@apollo/client/react/components';
import { assertNever } from '@tableau/data-structures';
import { DialogPositioningBehavior, DialogType, DialogWidget, TabStyles } from '@tableau/widgets';
import React from 'react';
import { Link } from 'react-router-dom';

import { Messages } from '../../../../../gen-src/Localize';
import {
  FlowFieldDetailsQuery,
  FlowFieldDetailsQueryVariables,
  FlowFieldDetailsDocument
} from '../../../../../gen-src/queries';
import {
  divWithStyle,
  linkClass,
  SiteUrlsContext,
  vizPortalFontStyle
} from '../../../data-catalog-shared';
import { LabelWrapper } from '../Labels/LabelsHookHelper';
import { RenderQueryResult } from '../renderQueryResult';
import { flowFieldDetailsLabelsStyle } from '../style/ColumnAndFieldDetailsStyles';

const dialogWidth = 500;

const DetailsPane = divWithStyle('DetailsPane', {
  ...vizPortalFontStyle,
  fontSize: TabStyles.Typography.Sizing.Default,
});

export const DetailsName = divWithStyle('DetailsName', {
  fontSize: TabStyles.Typography.Sizing.XLarge,
});

const DetailsProperties = divWithStyle('DetailsProperties', {
  display: 'table',
  paddingTop: TabStyles.Spacing.DefaultSpace,
});

const DetailsProperty = divWithStyle('DetailsProperty', {
  display: 'table-row',
});

const DetailsLabel = divWithStyle('DetailsLabel', {
  display: 'table-cell',
  padding: TabStyles.Spacing.DefaultSpace,
  color: TabStyles.Typography.Color.Secondary,
  textAlign: 'right',
});

const DetailsValue = divWithStyle('DetailsValue', {
  display: 'table-cell',
  padding: TabStyles.Spacing.DefaultSpace,
});

const DetailsPlaceholderMessage = divWithStyle('DetailsPlaceholderMessage', {
  paddingTop: TabStyles.Spacing.DefaultSpace,
});

type FlowFieldDetailsQuery_flowOutputSteps_outputFields = Unpacked<Unpacked<FlowFieldDetailsQuery['flowOutputSteps']>['outputFields']>

export interface FlowFieldDetailsDialogProps {
  readonly flowOutputStepId: string;
  readonly flowFieldId: string;
  readonly onClose: () => void;
}

export class FlowFieldDetailsDialog extends React.Component<FlowFieldDetailsDialogProps> {

  public override render(): JSX.Element {
    return (
      <Query<FlowFieldDetailsQuery, FlowFieldDetailsQueryVariables>
        query={FlowFieldDetailsDocument}
        variables={{ flowOutputStepId: this.props.flowOutputStepId, flowFieldId: this.props.flowFieldId }}
      >
        {result => <RenderQueryResult result={result} renderers={{ renderData: this.renderFlowFieldDetailsDialog }} />}
      </Query>
    );
  }

  private readonly renderFlowFieldDetailsDialog = (data: FlowFieldDetailsQuery): React.ReactNode => {
    const flowOutputStep = data.flowOutputSteps && data.flowOutputSteps[0];
    if (!flowOutputStep) {
      return null;
    }
    const flowField = flowOutputStep.outputFields && flowOutputStep.outputFields[0];
    if (!flowField) {
      return null;
    }

    return (
      <DialogWidget
        testId='flow-field-details'
        title={Messages.FieldDetails()}
        type={DialogType.NormalWithTitleBar}
        positioningBehavior={DialogPositioningBehavior.Centered}
        dialogWidth={dialogWidth}
        showCloseButton={true}
        showFooter={false}
        isModal={true}
        onCloseRequest={this.props.onClose}
        handleGlassClick={this.props.onClose}
      >
        <DetailsPane>
          <DetailsName>{flowField.name}</DetailsName>
          <LabelWrapper itemInfo={flowField} category={'all'} isEager={true} kind={'chip'} wrapper={(e) => 
            <span> 
              <span style={flowFieldDetailsLabelsStyle}>{e}</span>
            </span>
          }/>
          {this.renderProperties(flowField)}
        </DetailsPane>
      </DialogWidget>
    );
  }

  private renderProperties(flowField: FlowFieldDetailsQuery_flowOutputSteps_outputFields): React.ReactNode {
    switch (flowField.__typename) {
      case 'FlowColumnOutputField':
        if (flowField.column) {
          if (flowField.column.name !== null) {
            const column = flowField.column;
            const table = column.table;
            // The table for a column should always be defined.
            const tableId = table ? table.id : '';
            const columnName = column.name !== null ? column.name : Messages.PermissionsRequired();
            return (
              <DetailsProperties>
                <DetailsProperty>
                  <DetailsLabel>{Messages.FlowFieldDetails_OutputsToColumn()}</DetailsLabel>
                  <DetailsValue>
                    <SiteUrlsContext.Consumer>
                      {urls =>
                        <Link
                          className={linkClass}
                          to={urls.catalogTableColumnPath(tableId, column.id)}
                          title={columnName}
                        >
                          {columnName}
                        </Link>
                      }
                    </SiteUrlsContext.Consumer>
                  </DetailsValue>
                </DetailsProperty>
              </DetailsProperties>
            );
          } else {
            return (
              <DetailsPlaceholderMessage>
                {Messages.PermissionsRequired()}
              </DetailsPlaceholderMessage>
            );
          }
        } else {
          return (
            <DetailsPlaceholderMessage>
              {Messages.FlowFieldDetails_OutputColumnUnavailable()}
            </DetailsPlaceholderMessage>
          );
        }
      case 'FlowFieldOutputField':
        if (flowField.field) {
          if (flowField.field.name !== null) {
            const field = flowField.field;
            const datasource = field.datasource;
            // The datasource for a flow output field should always be defined,
            // if the field edge is, and it should always be a published data source,
            // since flows can't output workbooks.
            const datasourceVizportalUrlId =
              datasource && datasource.__typename === 'PublishedDatasource' ? datasource.vizportalUrlId : '';
            const fieldName = field.name !== null ? field.name : Messages.PermissionsRequired();
            return (
              <DetailsProperties>
                <DetailsProperty>
                  <DetailsLabel>{Messages.FlowFieldDetails_OutputsToField()}</DetailsLabel>
                  <DetailsValue>
                    <SiteUrlsContext.Consumer>
                      {urls =>
                        <Link
                          className={linkClass}
                          to={urls.datasourceLineageFieldPath(datasourceVizportalUrlId, field.id)}
                          title={fieldName}
                        >
                          {fieldName}
                        </Link>
                      }
                    </SiteUrlsContext.Consumer>
                  </DetailsValue>
                </DetailsProperty>
              </DetailsProperties>
            );
          } else {
            return (
              <DetailsPlaceholderMessage>
                {Messages.PermissionsRequired()}
              </DetailsPlaceholderMessage>
            );
          }
        } else {
          return (
            <DetailsPlaceholderMessage>
              {Messages.FlowFieldDetails_OutputFieldUnavailable()}
            </DetailsPlaceholderMessage>
          );
        }
      default:
        assertNever(flowField);
        return null;
    }
  }

}
